import { ChangeEvent, useState } from "react";
import {
  CheckIcon,
  EditIcon,
  FolderIcon,
  FoldersIcon,
  IconButton,
  Paragraph,
  TextInput,
  TrashCanIcon,
} from "@datagame/ui";
import { useMutation } from "@apollo/client";
import classNames from "classnames/bind";

import { DIRECTORY_UPDATE } from "../../../../../../../graphql/mutations/FolderMutations";
import { type Folder } from "../../FoldersList";
import { FolderProps } from "../FolderGroup";
import { DeleteFolderModal } from "../../../../../../shared/modals/DeleteFolderModal/DeleteFolderModal";

import styles from "./FolderItem.module.scss";
import { useDrag } from "react-dnd";

const cx = classNames.bind(styles);

export type FolderWithChildren = Folder & {
  children?: Folder[];
};

export const FolderItem = ({ folder, selected, setSelected, isEditable, className }: FolderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "FOLDER",
    item: folder,
    canDrag: !folder.default && (folder.children || []).length === 0,
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  }));
  const [updateDirectory] = useMutation(DIRECTORY_UPDATE);
  const canBeExpanded = Boolean(folder.children?.length);
  const folderClassName = cx("folder", className, {
    isDragging,
    virtual: folder.virtual,
    selected: selected?.id === folder.id,
    edit: isEditing,
  });

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleSave = () => {
    setIsEditing(false);

    updateDirectory({
      variables: {
        directoryId: folder.id,
        data: {
          name,
        },
      },
    });
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const renderName = () => {
    if (isEditing) {
      return <TextInput value={name} onChange={handleNameChange} size="small" icon={<FolderIcon />} />;
    }

    return <Paragraph>{folder.name}</Paragraph>;
  };

  const renderEditIcon = () => {
    if (isEditing) {
      return <IconButton style="outline" className={cx("actionIcon")} icon={<CheckIcon />} onClick={handleSave} />;
    }
    return (
      !folder.default && (
        <IconButton className={cx("actionIcon")} icon={<EditIcon />} onClick={() => setIsEditing(true)} />
      )
    );
  };

  return (
    <>
      <div
        className={folderClassName}
        onClick={() => setSelected?.(folder)}
        onKeyDown={() => setSelected?.(folder)}
        role="button"
        tabIndex={0}
        ref={drag}
      >
        <div className={styles.nameWrapper}>
          {canBeExpanded ? <FoldersIcon className={styles.folderIcon} /> : <FolderIcon className={styles.folderIcon} />}
          {renderName()}
        </div>
        {isEditable && (
          <div className={styles.actionsWrapper}>
            {renderEditIcon()}

            {!folder.default && (
              <IconButton className={cx("actionIcon", "deleteIcon")} icon={<TrashCanIcon />} onClick={handleDelete} />
            )}
          </div>
        )}
      </div>

      {isDeleteModalOpen && <DeleteFolderModal folder={folder} onClose={() => setIsDeleteModalOpen(false)} />}
    </>
  );
};
